import { useEffect, useState } from "react";
import { Edit3, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import FileUpload from "../../FileUpload";
import { useConfirm } from "../../ConfirmDialog";

/**
 * Iter-27.63 — Onglet "Histoires d'impact" : CRUD complet.
 * Utilisé dans le backoffice `/admin/espace-partenaires`.
 */
export default function ImpactStoriesTab({ assoId, useAssoEndpoints = false }) {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(false);
    const confirm = useConfirm();

    // Iter-27.64 — endpoints scopés selon le rôle
    const listUrl = useAssoEndpoints
        ? "/asso/impact-stories"
        : `/admin/associations/${assoId}/impact-stories`;
    const createUrl = useAssoEndpoints
        ? "/asso/impact-stories"
        : `/admin/associations/${assoId}/impact-stories`;
    const updateUrl = (sid) =>
        useAssoEndpoints ? `/asso/impact-stories/${sid}` : `/admin/impact-stories/${sid}`;
    const deleteUrl = (sid) =>
        useAssoEndpoints ? `/asso/impact-stories/${sid}` : `/admin/impact-stories/${sid}`;

    const reload = async () => {
        setLoading(true);
        try {
            const r = await api.get(listUrl);
            setItems(r.data || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assoId, useAssoEndpoints]);

    const save = async (story) => {
        try {
            if (story.story_id) {
                await api.patch(updateUrl(story.story_id), story);
                toast.success("Histoire mise à jour");
            } else {
                await api.post(createUrl, story);
                toast.success("Histoire publiée");
            }
            setEditing(null);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        }
    };

    const remove = async (sid) => {
        const story = items.find((it) => it.story_id === sid);
        const ok = await confirm({
            title: "Supprimer cette histoire d'impact ?",
            description: story?.title
                ? `« ${story.title} » sera retirée de l'espace ambassadeurs. Cette action est irréversible.`
                : "Cette histoire d'impact sera retirée de l'espace ambassadeurs. Cette action est irréversible.",
            confirmLabel: "Supprimer",
            cancelLabel: "Annuler",
            tone: "danger",
        });
        if (!ok) return;
        try {
            await api.delete(deleteUrl(sid));
            toast.success("Histoire supprimée");
            reload();
        } catch {
            toast.error("Erreur");
        }
    };

    if (editing) {
        return (
            <ImpactStoryForm story={editing} onCancel={() => setEditing(null)} onSave={save} />
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-white/55 font-light">
                    {items.length} histoire{items.length > 1 ? "s" : ""}{" "}
                    {items.length > 0 && (
                        <span className="text-shariti-orange-light">
                            ({items.filter((s) => s.is_published).length} publiée
                            {items.filter((s) => s.is_published).length > 1 ? "s" : ""})
                        </span>
                    )}
                </p>
                <button
                    onClick={() =>
                        setEditing({
                            title: "",
                            summary: "",
                            body: "",
                            cover_image_url: "",
                            outcome: "",
                            location: "",
                            year: new Date().getFullYear().toString(),
                            is_published: true,
                            pinned: false,
                        })
                    }
                    data-testid="impact-story-new"
                    className="bg-shariti-yellow text-shariti-black font-extrabold px-4 py-2 rounded-2xl text-sm"
                >
                    + Nouvelle histoire
                </button>
            </div>
            {loading ? (
                <p className="text-white/40 text-sm py-6 text-center">Chargement…</p>
            ) : items.length === 0 ? (
                <p
                    className="text-white/40 text-sm py-12 text-center"
                    data-testid="impact-stories-empty"
                >
                    Aucune histoire d'impact pour l'instant.
                </p>
            ) : (
                <div className="space-y-2">
                    {items.map((s) => (
                        <div
                            key={s.story_id}
                            data-testid={`impact-story-row-${s.story_id}`}
                            className={`flex items-center gap-3 p-3 rounded-2xl border ${
                                s.is_published
                                    ? "bg-white/3 border-white/10"
                                    : "bg-white/2 border-dashed border-white/15 opacity-60"
                            }`}
                        >
                            {s.cover_image_url && (
                                <img
                                    src={s.cover_image_url}
                                    alt=""
                                    className="w-14 h-14 rounded-xl object-cover shrink-0"
                                />
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-extrabold text-white truncate">
                                        {s.title}
                                    </span>
                                    {s.pinned && (
                                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-shariti-yellow/20 text-shariti-yellow font-extrabold">
                                            ★ À LA UNE
                                        </span>
                                    )}
                                    {!s.is_published && (
                                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/10 text-white/65">
                                            Brouillon
                                        </span>
                                    )}
                                </div>
                                {s.outcome && (
                                    <p className="text-[11px] text-shariti-orange-light font-bold mt-0.5">
                                        {s.outcome}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={() => setEditing(s)}
                                data-testid={`impact-story-edit-${s.story_id}`}
                                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/65"
                            >
                                <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => remove(s.story_id)}
                                data-testid={`impact-story-delete-${s.story_id}`}
                                className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 text-white/65 hover:text-red-300"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ImpactStoryForm({ story, onCancel, onSave }) {
    const [s, setS] = useState(story);
    return (
        <div className="space-y-4">
            <button
                onClick={onCancel}
                className="text-white/55 hover:text-white text-xs font-bold inline-flex items-center gap-1"
            >
                ← Retour à la liste
            </button>
            <div>
                <label className="text-[10px] font-thin tracking-[0.22em] uppercase text-white/45 mb-1 block">
                    Titre *
                </label>
                <input
                    type="text"
                    value={s.title}
                    onChange={(e) => setS({ ...s, title: e.target.value })}
                    placeholder="Ex: Maria, à nouveau libre après 12 ans"
                    maxLength={200}
                    data-testid="impact-story-title"
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white outline-none focus:border-shariti-orange/50"
                />
            </div>
            <div>
                <label className="text-[10px] font-thin tracking-[0.22em] uppercase text-white/45 mb-1 block">
                    Résumé (teaser)
                </label>
                <input
                    type="text"
                    value={s.summary}
                    onChange={(e) => setS({ ...s, summary: e.target.value })}
                    placeholder="Une phrase qui accroche"
                    maxLength={240}
                    data-testid="impact-story-summary"
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white outline-none focus:border-shariti-orange/50"
                />
            </div>
            <div>
                <label className="text-[10px] font-thin tracking-[0.22em] uppercase text-white/45 mb-1 block">
                    Récit complet *
                </label>
                <textarea
                    value={s.body}
                    onChange={(e) => setS({ ...s, body: e.target.value })}
                    placeholder="Raconte l'histoire en détail (markdown léger autorisé)"
                    rows={8}
                    maxLength={6000}
                    data-testid="impact-story-body"
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white outline-none focus:border-shariti-orange/50 resize-y"
                />
            </div>
            <div>
                <label className="text-[10px] font-thin tracking-[0.22em] uppercase text-white/45 mb-1 block">
                    Photo de couverture (full-bleed)
                </label>
                <FileUpload
                    kind="image"
                    currentUrl={s.cover_image_url}
                    onUploaded={(url) => setS({ ...s, cover_image_url: url })}
                    testId="impact-story-cover"
                />
            </div>
            <div className="grid grid-cols-3 gap-3">
                <div>
                    <label className="text-[10px] font-thin tracking-[0.22em] uppercase text-white/45 mb-1 block">
                        Outcome (chiffré)
                    </label>
                    <input
                        type="text"
                        value={s.outcome}
                        onChange={(e) => setS({ ...s, outcome: e.target.value })}
                        placeholder="1 200 enfants scolarisés"
                        data-testid="impact-story-outcome"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-3 py-2.5 text-sm text-white outline-none focus:border-shariti-orange/50"
                    />
                </div>
                <div>
                    <label className="text-[10px] font-thin tracking-[0.22em] uppercase text-white/45 mb-1 block">
                        Lieu
                    </label>
                    <input
                        type="text"
                        value={s.location}
                        onChange={(e) => setS({ ...s, location: e.target.value })}
                        placeholder="Bangladesh"
                        data-testid="impact-story-location"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-3 py-2.5 text-sm text-white outline-none focus:border-shariti-orange/50"
                    />
                </div>
                <div>
                    <label className="text-[10px] font-thin tracking-[0.22em] uppercase text-white/45 mb-1 block">
                        Année
                    </label>
                    <input
                        type="text"
                        value={s.year}
                        onChange={(e) => setS({ ...s, year: e.target.value })}
                        placeholder="2024"
                        data-testid="impact-story-year"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-3 py-2.5 text-sm text-white outline-none focus:border-shariti-orange/50"
                    />
                </div>
            </div>
            <div className="flex items-center gap-4">
                <label className="inline-flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={s.is_published !== false}
                        onChange={(e) => setS({ ...s, is_published: e.target.checked })}
                        data-testid="impact-story-published"
                        className="accent-shariti-orange"
                    />
                    <span className="text-white/85 font-bold">Publié</span>
                </label>
                <label className="inline-flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={!!s.pinned}
                        onChange={(e) => setS({ ...s, pinned: e.target.checked })}
                        data-testid="impact-story-pinned"
                        className="accent-shariti-yellow"
                    />
                    <span className="text-white/85 font-bold">À la une (pinned)</span>
                </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
                <button
                    onClick={onCancel}
                    className="bg-white/5 hover:bg-white/10 text-white/80 font-bold px-4 py-2.5 rounded-2xl"
                >
                    Annuler
                </button>
                <button
                    onClick={() => onSave(s)}
                    disabled={!s.title?.trim() || !s.body?.trim()}
                    data-testid="impact-story-save"
                    className="bg-shariti-yellow text-shariti-black font-extrabold px-5 py-2.5 rounded-2xl disabled:opacity-40"
                >
                    {story.story_id ? "Mettre à jour" : "Publier"}
                </button>
            </div>
        </div>
    );
}
